/**
 * RelayRecipientsPage
 * Recipient health table, filterable by tier.
 * Route: /settings/agent-email/recipients (a tab of SettingsAgentEmailPage)
 * US-5
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchRecipients, type RecipientHealth } from '../lib/relay.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { RecipientHealthBadge } from '../components/relay/RecipientHealthBadge';

type TierFilter = '' | 'healthy' | 'degraded' | 'quarantined';

const TIER_OPTIONS: { value: TierFilter; label: string }[] = [
  { value: '', label: 'All tiers' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'degraded', label: 'Degraded' },
  { value: 'quarantined', label: 'Quarantined' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 20;

export function RelayRecipientsPage() {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState<RecipientHealth[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [tierFilter, setTierFilter] = useState<TierFilter>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecipients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchRecipients({
        tier: tierFilter || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRecipients(data.recipients);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  }, [tierFilter, page]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const handleTierChange = useCallback((value: TierFilter) => {
    setTierFilter(value);
    setPage(1);
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="relay-recipients-page">
      {/* Filter bar */}
      <div className="mb-4 flex items-center gap-3">
        <label
          htmlFor="recipients-filter-tier"
          className="text-sm text-muted-foreground shrink-0"
        >
          Tier:
        </label>
        <select
          id="recipients-filter-tier"
          data-testid="recipients-filter-tier"
          value={tierFilter}
          onChange={(e) => handleTierChange(e.target.value as TierFilter)}
          className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground outline-none min-w-[140px]"
          aria-label="Filter by health tier"
        >
          {TIER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error state */}
      {error && (
        <div
          data-testid="recipients-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={loadRecipients} data-testid="recipients-retry">
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div data-testid="recipients-loading" className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {recipients.length === 0 ? (
            <div
              data-testid="recipients-empty"
              className="py-12 text-center text-muted-foreground"
            >
              <p className="text-sm">No recipients found.</p>
              <p className="text-xs mt-1">Recipients appear here once your agents send or receive messages. Use the tier filter above to focus on degraded or quarantined addresses.</p>
            </div>
          ) : (
            <div
              data-testid="recipients-table"
              className="rounded-lg border border-border overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Recipient health table">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Address
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Kind
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Health
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Bounces
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Complaints
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Last Inbound
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Last Outbound
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r, index) => (
                      <tr
                        key={r.id}
                        data-testid={`recipient-row-${r.address}`}
                        className={`hover:bg-accent transition-colors cursor-pointer ${
                          index < recipients.length - 1 ? 'border-b border-border' : ''
                        }`}
                        onClick={() => navigate(`/settings/agent-email/recipients/${encodeURIComponent(r.address)}`)}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-foreground">
                          <Link
                            to={`/settings/agent-email/recipients/${encodeURIComponent(r.address)}`}
                            className="hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
                            aria-label={`View recipient ${r.address}, health: ${r.healthTier}`}
                          >
                            {r.address}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground capitalize">
                          {r.kind}
                        </td>
                        <td className="px-4 py-3">
                          <RecipientHealthBadge tier={r.healthTier} />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-foreground">
                          {r.bounceCount}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-foreground">
                          {r.complaintCount}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {formatDate(r.lastInboundAt)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {formatDate(r.lastOutboundAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              data-testid="recipients-pagination"
              className="mt-4 flex items-center justify-between"
            >
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  data-testid="recipients-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="recipients-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
